import React from 'react';
import { Button } from 'rbx';
import qs from 'querystringify';
import { useStoreState } from 'easy-peasy';
import { toast } from 'react-toastify';

import Layout from '../../../components/global/Layout';
import Header from './Header';
import Search from './Search';
import { Pagination, Tabs } from '../../../components/elements';

import MenuServiceAuditTable from './MenuServiceAuditTable';
import { downloadCSVFile, usePlaceMenuServiceAudit } from './helpers';

const Index = ({
  history,
  // match,
  location,
}) => {
  const { userId: adminUserId } = useStoreState(state => state.auth);
  const [downloadCsv, setDownloadCsv] = React.useState(false);

  const routeState = qs.parse(location.search);
  const { page, cityFilter, nameFilter, serviceTypeFilter } = routeState;

  let city = '';
  let name = '';
  let serviceType = '';

  if (cityFilter) {
    city = cityFilter;
  }

  if (nameFilter) {
    name = nameFilter;
  }

  if (serviceTypeFilter) {
    serviceType = serviceTypeFilter;
  }

  const currentPage = parseInt(page, 10) - 1 >= 0 ? parseInt(page, 10) - 1 : 0;

  const [
    loading,
    placeMenuServiceAudit,
    pageCount,
    totalElements,
    fetchMenuAudit,
  ] = usePlaceMenuServiceAudit({
    adminUserId,
    city,
    name,
    serviceType,
    from: currentPage * 50,
    size: 50,
  });

  return (
    <Layout>
      <Header />
      <Search
        setCityName={v => {
          history.push(
            `/menu-service-audit${qs.stringify(
              {
                ...routeState,
                cityFilter: v,
                page: 0,
              },
              true,
            )}`,
          );
        }}
        setPlaceNameSearchInput={v => {
          history.push(
            `/menu-service-audit${qs.stringify(
              {
                ...routeState,
                nameFilter: v,
                page: 0,
              },
              true,
            )}`,
          );
        }}
        placeNameSearchInput={name}
        service={(() => {
          const st = [
            { label: 'ALL', value: 'All' },
            { label: 'PICKUP', value: 'Pickup' },
            { label: 'DELIVERY', value: 'Delivery' },
            { label: 'DINEIN', value: 'Dinein' },
          ];
          return st.find(ele => ele.value === serviceTypeFilter);
        })()}
        city={city}
        setService={v => {
          history.push(
            `/menu-service-audit${qs.stringify(
              {
                ...routeState,
                serviceTypeFilter: v.value,
                page: 0,
              },
              true,
            )}`,
          );
        }}
      />
      <Tabs className="tabs is-medium is-fullwidth">
        <div style={{ marginLeft: 'auto', padding: '1rem' }}>
          <div>
            <Button
              type="button"
              className={`button control ${downloadCsv && 'is-loading'}`}
              style={{
                marginLeft: '15px',
              }}
              onClick={async () => {
                setDownloadCsv(true);

                function generateArray(n) {
                  return Array.from(Array(n).keys());
                }

                function escapeCSVValue(value) {
                  if (value === null || value === undefined) return '';

                  if (typeof value === 'boolean')
                    return value ? 'true' : 'false';

                  const stringValue = String(value);

                  if (
                    stringValue.includes(',') ||
                    stringValue.includes('"') ||
                    stringValue.includes('\n')
                  ) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                  }

                  return stringValue;
                }

                function formatCurrency(amount) {
                  return amount
                    ? `$${parseFloat(amount).toFixed(2)}`
                    : 'Not available';
                }

                function processMenuItem(mua) {
                  return {
                    name: mua.name || '',
                    service_type: mua.service_type || '',
                    menu_name: mua.menu_name || '',
                    minimumOrderAmount: formatCurrency(
                      mua.minimum_order_amount,
                    ),
                    catering: mua?.is_catering_service || '-',
                    payment_method: (mua?.payment_method || '').replace(
                      /,/g,
                      ' ',
                    ),
                    auto_confirm: mua.auto_confirm,
                    auto_ready: mua.auto_ready,
                    auto_cancel: mua?.auto_cancel?.is_auto_cancel,
                    auto_cancel_time_in_mins:
                      mua?.auto_cancel?.is_auto_cancel &&
                      mua?.auto_cancel?.time_in_mins
                        ? mua?.auto_cancel?.time_in_mins
                        : '',
                    delivery_channel:
                      mua.service_type === 'Delivery' && mua?.delivery_channel
                        ? mua?.delivery_channel
                        : ' ',
                    api_key: mua?.api_key || '',
                    preparation_estimate: mua?.preparation_estimate || '',
                    zone: mua?.zone_fee
                      ? mua.zone_fee
                          .map(
                            ele =>
                              `${ele.name}: ${
                                ele.delivery_estimate
                              } min(s) ${formatCurrency(ele?.delivery_fee)}`,
                          )
                          .join(' | ')
                      : '-',
                    schedule_in_advance: mua.schedule_in_advance,
                    schedule_order_when_closed: mua.schedule_order_when_closed,
                  };
                }

                try {
                  const firstPageResponse = await fetchMenuAudit({
                    _adminUserId: adminUserId,
                    _from: 0,
                    _name: name,
                    _city: city,
                    _serviceType: serviceType,
                  });

                  if (!firstPageResponse) {
                    throw new Error('No response from the api!');
                  }

                  const totalPages = firstPageResponse.total_pages || 1;
                  const pageSize = 50;

                  let allMenuItems = (
                    firstPageResponse?.search_place_menu_services_list || []
                  ).map(processMenuItem);

                  if (totalPages > 1) {
                    const pageIndexes = generateArray(totalPages).slice(1);

                    const remainingPagesResponses = await Promise.all(
                      pageIndexes.map(pageIndex =>
                        fetchMenuAudit({
                          _adminUserId: adminUserId,
                          _from: pageIndex * pageSize,
                          _name: name,
                          _city: city,
                          _serviceType: serviceType,
                        }),
                      ),
                    );

                    const remainingItems = remainingPagesResponses
                      .flatMap(
                        response =>
                          response?.search_place_menu_services_list || [],
                      )
                      .map(processMenuItem);

                    allMenuItems = [...allMenuItems, ...remainingItems];
                  }

                  if (allMenuItems.length === 0) {
                    throw new Error('No menu audit data available');
                  }

                  const headers = Object.keys(allMenuItems[0]);
                  const headerRow = headers.map(escapeCSVValue).join(',');

                  const dataRows = allMenuItems.map(item =>
                    headers
                      .map(header => escapeCSVValue(item[header]))
                      .join(','),
                  );

                  const csvContent = [headerRow, ...dataRows].join('\n');

                  downloadCSVFile(
                    csvContent,
                    `menu-audit-${new Date().toISOString()}.csv`,
                  );
                } catch (error) {
                  toast.error(
                    `Failed to download CSV: ${error.message ||
                      'Unknown error'}`,
                  );
                } finally {
                  setDownloadCsv(false);
                }
              }}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Tabs>
      {totalElements > 0 && (
        <Pagination
          pageFrom={currentPage * 50}
          pageCount={pageCount}
          listSize={50}
          totalElements={totalElements}
          currentPage={currentPage}
          handlePageClick={value => {
            history.push(
              `/menu-service-audit${qs.stringify(
                {
                  ...routeState,
                  page: value.selected + 1,
                },
                true,
              )}`,
            );
          }}
        />
      )}
      <MenuServiceAuditTable
        onClick={() => {
          console.log('routing to service type settings...');
        }}
        loading={loading}
        placeMenuServiceAudit={placeMenuServiceAudit}
      />
    </Layout>
  );
};

export default Index;
